import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReportDataService from '../../services/report.service';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCurrencySync } from '../../utils/currencyUtils';

const TotalSalesDashboard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      
      const response = await ReportDataService.getTotalSales(params);
      if (response.data && response.data.data) {
        setData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('reports.errorLoading'));
      console.error('Error loading total sales:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadData();
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setTimeout(loadData, 100);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <AttachMoneyIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h5" component="h2">
              {t('reports.totalSales')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('reports.totalSalesDescription')}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('reports.startDate')}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('reports.endDate')}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" gap={1}>
                <Button variant="contained" onClick={handleFilter} startIcon={<SearchIcon />}>
                  {t('reports.filter')}
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  {t('reports.reset')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {data && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('reports.totalSalesAmount')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrencySync(data.total_sales || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('reports.totalQuantity')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {parseFloat(data.total_quantity || 0).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('reports.totalTransactions')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {data.total_count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {data.sales_by_date && data.sales_by_date.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('reports.salesByDate')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('reports.date')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('reports.total')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('reports.quantity')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('reports.transactions')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.sales_by_date.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{item.date}</TableCell>
                          <TableCell align="right">
                            {formatCurrencySync(item.total || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {parseFloat(item.quantity || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">{item.count || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default TotalSalesDashboard;

